import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Database, Users, Trophy, Calendar, CheckCircle, Trash2 } from 'lucide-react';
import { importRealData } from '../utils/importRealData';
import { clearAllData } from '../utils/clearData';

const DataInitializer = ({ onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState('');

  const handleInitialize = async (clearFirst = false) => {
    setLoading(true);
    setError('');

    try {
      if (clearFirst) {
        console.log('Clearing existing data first...');
        await clearAllData();
      }
      
      await importRealData();
      setCompleted(true);
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      setError('An error occurred while initializing data.');
      console.error('Data initialization error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (completed) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <CheckCircle size={64} className="mx-auto mb-4 text-green-500" />
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#1C563D' }}>
          Sample Data Created!
        </h2>
        <p className="text-gray-600 mb-4">
          Teams, players, and fixtures have been added to your database.
        </p>
        <div className="text-sm text-gray-500">
          Redirecting to dashboard...
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="card">
        <div className="text-center mb-6">
          <Database size={48} className="mx-auto mb-4" style={{ color: '#1C563D' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#1C563D' }}>
            Initialize Sample Data
          </h2>
          <p className="text-gray-600">
            Populate your database with sample teams, players, and fixtures for demo purposes.
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
            <Users size={20} className="mr-3 text-green-600" />
            <div>
              <div className="font-medium text-green-800">6 Teams</div>
              <div className="text-sm text-green-600">Aztecs, El Matadors, Gauchos, Pharoahs, Romans, Sultans</div>
            </div>
          </div>

          <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Users size={20} className="mr-3 text-blue-600" />
            <div>
              <div className="font-medium text-blue-800">72 Players</div>
              <div className="text-sm text-blue-600">Complete player profiles with Playtomic ratings and stats</div>
            </div>
          </div>

          <div className="flex items-center p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <Trophy size={20} className="mr-3 text-purple-600" />
            <div>
              <div className="font-medium text-purple-800">Tier System</div>
              <div className="text-sm text-purple-600">Tier 1, Tier 2, and Tier 3 categories with Marquee players</div>
            </div>
          </div>

          <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Calendar size={20} className="mr-3 text-yellow-600" />
            <div>
              <div className="font-medium text-yellow-800">Real Data</div>
              <div className="text-sm text-yellow-600">Actual league data with Playtomic ratings and game history</div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex gap-4">
            <button
              onClick={() => handleInitialize(false)}
              disabled={loading}
              className="btn btn-primary flex-1"
              style={{
                backgroundColor: loading ? '#999' : '#1C563D',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Importing Data...' : 'Import Real Data'}
            </button>
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={() => handleInitialize(true)}
              disabled={loading}
              className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <Trash2 size={16} />
              {loading ? 'Clearing & Importing...' : 'Clear Old Data & Import'}
            </button>
            <button
              onClick={() => onComplete()}
              disabled={loading}
              className="btn btn-secondary flex-1"
            >
              Skip for Now
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DataInitializer;
